import { useEffect, useState } from 'react'
import { useSnapshot } from 'valtio'
import { CurrentUserAssessmentState, ScoringState } from '../../storage'
import { getScoringBySubtest } from '../../firebase/api/firestore/scoringBySubtest'
import { SubScore } from '../../firebase/api/types'
import { GroupScoreSummarySubScore } from '@app/types'

type UseWritingScoresReturn = {
  loading: boolean
  score: string | number | null
  scorePending: boolean
  subScores: SubScore[]
}

const toSubScore = (subScore: GroupScoreSummarySubScore): SubScore => ({
  name: subScore.name,
  raw: subScore.scale,
  scale: subScore.scale,
})

export const useWritingScores = (): UseWritingScoresReturn => {
  const { subject, userAssessmentId } = useSnapshot(CurrentUserAssessmentState)
  const { groupMode, groupTestScoring } = useSnapshot(ScoringState)
  const [loading, setLoading] = useState(false)
  const [score, setScore] = useState<string | number | null>(null)
  const [scorePending, setScorePending] = useState(false)
  const [subScores, setSubScores] = useState<SubScore[]>([])

  useEffect(() => {
    if (groupMode) {
      const scoreSummary = groupTestScoring?.scoreSummary
      setLoading(false)
      setScore(scoreSummary?.score ?? null)
      setScorePending(false)
      setSubScores(scoreSummary?.subScores ? scoreSummary.subScores.map(toSubScore) : [])
      return
    }

    if (!userAssessmentId) return

    let cancelled = false
    setLoading(true)

    getScoringBySubtest(userAssessmentId)
      .then(doc => {
        if (cancelled) return
        if (doc?.bySubtest?.subScores?.length) {
          setScore(doc.bySubtest.scale)
          setScorePending(false)
          setSubScores(doc.bySubtest.subScores)
        } else {
          setScore(null)
          setScorePending(
            subject?.toLowerCase() === 'writing' &&
              (!doc || doc.calculation_status?.toLowerCase() === 'pending' || doc.complete === false),
          )
          setSubScores([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [groupMode, groupTestScoring, subject, userAssessmentId])

  return { loading, score, scorePending, subScores }
}
